/// <amd-module name="VSSUI/Components/PivotBar/PivotCommandBar" />

import * as React from "react";

import { CommandBar, ICommandBar } from "OfficeFabric/CommandBar";
import { IContextualMenuItem } from "OfficeFabric/ContextualMenu";
import { BaseComponent } from "OfficeFabric/Utilities";
import { arrayEquals, css } from "VSSUI/Util";

import { IObservableArray } from "VSS/Core/Observable";
import { mapActionsToItems } from "../../Components/PivotBar/PivotBarAction";
import {
    IOnOffViewActionProps,
    IPivotBarAction,
    IPivotBarViewAction,
    PivotBarViewActionArea,
    PivotBarViewActionType
} from "../../Components/PivotBar/PivotBarAction.Props";
import { IPivotCommandBar, IPivotCommandBarProps } from "../../Components/PivotBar/PivotCommandBar.Props";

import * as VSSUI_Resources from "VSSUI/VSS.Resources.VSSUI";

/** @deprecated */
export interface IPivotCommandBarState {
    commands?: IPivotBarAction[];
    viewActions?: IPivotBarViewAction[];
}

/** @deprecated */
export class PivotCommandBar extends BaseComponent<IPivotCommandBarProps, IPivotCommandBarState> implements IPivotCommandBar {
    private _commandBar: ICommandBar;

    constructor(props: IPivotCommandBarProps) {
        super(props);

        this.state = {
            commands: this._getCommands(this.props),
            viewActions: this._getViewActions(this.props)
        };
    }

    public componentWillReceiveProps(nextProps: IPivotCommandBarProps) {
        let changed = false;
        if (!arrayEquals(this.props.commands, nextProps.commands)) {
            this._unsubscribeItems(this.props.commands, this._onCommandsChanged);
            this._subscribeItems(nextProps.commands, this._onCommandsChanged);
            changed = true;
        }

        if (!arrayEquals(this.props.viewActions, nextProps.viewActions)) {
            this._unsubscribeItems(this.props.viewActions, this._onViewActionsChanged);
            this._subscribeItems(nextProps.viewActions, this._onViewActionsChanged);
            changed = true;
        }

        if (this.props.showFilterAction !== nextProps.showFilterAction || this.props.filterBar !== nextProps.filterBar) {
            changed = true;
        }

        if (changed) {
            this.setState({
                commands: this._getCommands(nextProps),
                viewActions: this._getViewActions(nextProps)
            });
        }
    }

    public componentDidMount() {
        this._subscribeItems(this.props.commands, this._onCommandsChanged);
        this._subscribeItems(this.props.viewActions, this._onViewActionsChanged);
    }

    public componentWillUnmount() {
        this._unsubscribeItems(this.props.commands, this._onCommandsChanged);
        this._unsubscribeItems(this.props.viewActions, this._onViewActionsChanged);
    }

    public render(): JSX.Element | null {
        const {
            className,
            viewOptions,
            filterBar,
            hasAppliedFilter,
            showFilterAction,
            onShowFilterBarClicked,
            showFilterBarViewOptionKey,
            prependSeparator,
            role,
            ariaLabel,
            commandsMenuBarAriaLabel,
            inlineFilterAriaLabel
        } = this.props;

        const { commands, viewActions } = this.state;

        // Set up commands first by keeping important commands visible and the rest under the overflow menu (...)
        let commandItems: IContextualMenuItem[] = [];
        let commandOverflowItems: IContextualMenuItem[] = [];
        if (commands && commands.length) {
            const importantCommands = commands.filter(c => c.important);
            const overflowCommands = commands.filter(c => !c.important);

            // Important commands are always visible
            commandItems = mapActionsToItems(importantCommands, viewOptions, this._forceUpdate);

            // Non-important commands go to overflow section
            commandOverflowItems = mapActionsToItems(overflowCommands, viewOptions, this._forceUpdate);
        }

        // Set up view actions the same way we do for commands
        // Instead of (...), non important view actions go under 'View Options'
        const farActions: IPivotBarViewAction[] = [];

        if (viewActions && viewActions.length) {
            // Add important view actions first which target BEFORE the view options entry.
            farActions.push(...viewActions.filter(va => va.important && va.viewActionRenderArea === PivotBarViewActionArea.beforeViewOptions));

            // Add non-important view actions into a "view options" entry.
            const overflowViewActions = viewActions.filter(va => !va.important);
            if (overflowViewActions.length) {
                farActions.push({
                    key: "viewOptions",
                    title: VSSUI_Resources.PivotBarViewOptionsText,
                    ariaLabel: VSSUI_Resources.PivotBarViewOptionsText,
                    actionProps: { className: "vss-PivotBar--viewOptionsMenu" },
                    important: true,
                    actionType: PivotBarViewActionType.Command,
                    iconProps: { iconName: "Equalizer" },
                    children: overflowViewActions
                });
            }

            // Add important view actions which target AFTER the view options entry
            farActions.push(
                ...viewActions.filter(
                    va =>
                        va.important &&
                        va.viewActionRenderArea !== PivotBarViewActionArea.beforeViewOptions &&
                        va.viewActionRenderArea !== PivotBarViewActionArea.farRight
                )
            );
        }

        // Add filter view action automatically when a filterbar exists in the children
        if (showFilterAction && !filterBar) {
            farActions.push({
                key: showFilterBarViewOptionKey,
                important: true,
                actionType: PivotBarViewActionType.OnOff,
                actionProps: {
                    showText: false,
                    onAriaLabel: VSSUI_Resources.PivotBarFilterActionTextHide,
                    offAriaLabel: VSSUI_Resources.PivotBarFilterActionTextShow,
                    onIconProps: { iconName: hasAppliedFilter ? "FilterSolid" : "Filter" },
                    offIconProps: { iconName: hasAppliedFilter ? "FilterSolid" : "Filter" }
                } as IOnOffViewActionProps,
                onClick: onShowFilterBarClicked
            });
        }

        // Add far-right important view actions last
        if (viewActions && viewActions.length) {
            farActions.push(...viewActions.filter(va => va.important && va.viewActionRenderArea === PivotBarViewActionArea.farRight));
        }

        const viewActionItems = mapActionsToItems(farActions, viewOptions, this._forceUpdate);

        // If available, show inline filterbar to the left of all view actions
        if (filterBar) {
            viewActionItems.unshift({
                className: "vss-PivotBar--inlineFilterBar",
                key: "filterBar",
                ariaLabel: inlineFilterAriaLabel,
                onRender: () => {
                    return filterBar;
                }
            });
        }

        if (commandItems.length || commandOverflowItems.length || viewActionItems.length) {
            return (
                <div className="vss-PivotBar--right" role={role} aria-label={ariaLabel}>
                    {!!(prependSeparator && (commandItems.length || commandOverflowItems.length)) && <div className="vss-PivotBar--separator" />}
                    <CommandBar
                        componentRef={commandBar => {
                            this._commandBar = commandBar;
                        }}
                        className={css(className)}
                        items={commandItems}
                        overflowItems={commandOverflowItems}
                        farItems={viewActionItems}
                        elipisisAriaLabel={VSSUI_Resources.PivotBarMoreCommandsAriaLabel}
                        ariaLabel={commandsMenuBarAriaLabel}
                    />
                </div>
            );
        }

        return null;
    }

    public focus(): void {
        if (this._commandBar) {
            this._commandBar.focus();
        }
    }

    private _subscribeItems(itemSources: IObservableArray<IPivotBarAction>[], observer: () => void): void {
        (itemSources || []).forEach(source => source.subscribe(observer));
    }

    private _unsubscribeItems(itemSources: IObservableArray<IPivotBarAction>[], observer: () => void): void {
        (itemSources || []).forEach(source => source.unsubscribe(observer));
    }

    private _getCommands(props: IPivotCommandBarProps): IPivotBarAction[] {
        const commands: IPivotBarAction[] = [];
        const pivotCommands = props.commands || [];
        for (const pc of pivotCommands) {
            commands.push(...pc.value);
        }

        return commands;
    }

    private _getViewActions(props: IPivotCommandBarProps): IPivotBarViewAction[] {
        const viewActions: IPivotBarViewAction[] = [];
        const pivotViewActions = props.viewActions || [];
        for (const pv of pivotViewActions) {
            viewActions.push(...pv.value);
        }

        return viewActions;
    }

    private _onCommandsChanged = () => {
        this.setState({ commands: this._getCommands(this.props) });
    };

    private _onViewActionsChanged = () => {
        this.setState({ viewActions: this._getViewActions(this.props) });
    };

    /**
     * Conveniently autobound forceUpdate method.
     */
    private _forceUpdate = () => {
        this.forceUpdate();
    };
}
